import {
  ImpersonationGrantLevel,
  ImpersonationSessionWithToken,
  Session
} from "@wingspanhq/wingspan-api-sdk";

const REFRESH_TOKEN_STORAGE_KEY = "ws:v3:refresh_token";
const PREFERRED_ACCOUNT_KEY_PREFIX = "ws:v3:preferred_account:";
const IMPERSONATION_STORAGE_KEY = "ws:v3:impersonation";

/**
 * `sessionId` is absent while impersonating: an impersonation session's bearer
 * id lives only inside the token, and the id the create response does expose
 * identifies the audit row instead (see `Impersonation.impersonationSessionId`).
 * Callers that need to act on the session itself must use `getOwnIdentity`.
 *
 * `personId` is absent while impersonating too — impersonation tokens are
 * Account-bound by contract and carry no Person principal at all.
 */
export type SessionIdentity = {
  sessionId?: string;
  personId?: string;
  accountId?: string;
};

/**
 * The active impersonation, as persisted. Impersonation tokens are short-lived
 * and come with no refresh token, so the whole token is stored (unlike the
 * regular access token, which is memory-only and re-minted from the refresh
 * token) — otherwise a page reload would drop the impersonation.
 *
 * Storage is `sessionStorage`: the impersonation stays scoped to the tab that
 * started it and cannot outlive the browser session.
 */
export type Impersonation = {
  token: string;
  /**
   * The audit row's id — what `ImpersonationSession.id` carries and what
   * `/v3/platform/impersonation-sessions/{impersonationSessionId}` takes. It is
   * deliberately NOT the bearer session id: that one is only a claim inside
   * `token`, and passing this id to the Sessions API addresses nothing.
   */
  impersonationSessionId: string;
  /**
   * Always present: `setImpersonation` refuses the union's Person arm outright,
   * because the whole impersonated shell is keyed on an Account. Person-target
   * support is tracked separately (WIN-15843).
   */
  targetAccountId: string;
  level: ImpersonationGrantLevel;
  expiresAt?: string;
};

const parseImpersonation = (raw: string | null): Impersonation | undefined => {
  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as Impersonation;
    // Both are what make a persisted impersonation usable: the token to send,
    // and the Account the shell binds to.
    return parsed.token && parsed.targetAccountId ? parsed : undefined;
  } catch {
    return undefined;
  }
};

class TokenStore {
  private accessToken: string | undefined;
  private identity: SessionIdentity | undefined;
  private impersonation: Impersonation | undefined;
  private persisted = false;
  private listeners = new Set<() => void>();

  private storageFor(persist: boolean): Storage {
    return persist ? window.localStorage : window.sessionStorage;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    this.listeners.forEach(listener => listener());
  }

  getAccessToken(): string | undefined {
    return this.getImpersonation()?.token ?? this.accessToken;
  }

  getIdentity(): SessionIdentity | undefined {
    const impersonation = this.getImpersonation();
    if (impersonation) {
      return { accountId: impersonation.targetAccountId };
    }

    return this.identity;
  }

  /** The admin identity underneath an impersonation, ignoring the override. */
  getOwnIdentity(): SessionIdentity | undefined {
    return this.identity;
  }

  getRefreshToken(): string | undefined {
    const fromLocal = window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (fromLocal) {
      this.persisted = true;
      return fromLocal;
    }

    const fromSession = window.sessionStorage.getItem(
      REFRESH_TOKEN_STORAGE_KEY
    );
    if (fromSession) {
      this.persisted = false;
      return fromSession;
    }

    return undefined;
  }

  setSession(session: Session, persist?: boolean): void {
    if (typeof persist === "boolean") {
      this.persisted = persist;
    }

    this.accessToken = session.token;
    this.identity = {
      sessionId: session.id,
      personId: session.personId ?? undefined,
      accountId: session.boundAccountId ?? undefined
    };

    if (session.refreshToken) {
      window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      window.sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      this.storageFor(this.persisted).setItem(
        REFRESH_TOKEN_STORAGE_KEY,
        session.refreshToken
      );
    }

    this.notify();
  }

  /**
   * Overrides the active token with an impersonation token. The underlying
   * admin session (its refresh token above all) is left untouched, which is
   * what makes `clearImpersonation` a complete way back.
   */
  setImpersonation(result: ImpersonationSessionWithToken): void {
    // The SDK models the target as `targetAccountId` XOR `targetPersonId`. The
    // app only ever mints the Account arm, and the whole impersonated shell is
    // keyed on that Account, so a Person-target session is unusable here —
    // refuse it loudly instead of storing a session with nothing to bind to.
    const targetAccountId = result.session.targetAccountId;

    if (!targetAccountId) {
      throw new Error("Impersonation session has no target account");
    }

    const impersonation: Impersonation = {
      token: result.token,
      impersonationSessionId: result.session.id,
      targetAccountId,
      level: result.session.level,
      expiresAt: (result.expiresAt ?? result.session.expiresAt)?.toISOString()
    };

    this.impersonation = impersonation;
    window.sessionStorage.setItem(
      IMPERSONATION_STORAGE_KEY,
      JSON.stringify(impersonation)
    );

    this.notify();
  }

  getImpersonation(): Impersonation | undefined {
    if (!this.impersonation) {
      this.impersonation = parseImpersonation(
        window.sessionStorage.getItem(IMPERSONATION_STORAGE_KEY)
      );
    }

    return this.impersonation;
  }

  isImpersonating(): boolean {
    return !!this.getImpersonation();
  }

  /** Drops the impersonation, restoring the admin session that started it. */
  clearImpersonation(): void {
    this.impersonation = undefined;
    window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);

    this.notify();
  }

  clear(): void {
    this.accessToken = undefined;
    this.identity = undefined;
    this.impersonation = undefined;
    window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    window.sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);

    this.notify();
  }

  getPreferredAccountId(personId: string): string | undefined {
    return (
      window.localStorage.getItem(PREFERRED_ACCOUNT_KEY_PREFIX + personId) ??
      undefined
    );
  }

  setPreferredAccountId(personId: string, accountId: string): void {
    window.localStorage.setItem(
      PREFERRED_ACCOUNT_KEY_PREFIX + personId,
      accountId
    );
  }

  clearPreferredAccountId(personId: string): void {
    window.localStorage.removeItem(PREFERRED_ACCOUNT_KEY_PREFIX + personId);
  }
}

export const tokenStore = new TokenStore();
